/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { Col, Row } from "react-awesome-styled-grid";
import { Else, If, Then, When } from "react-if";
import { ExtendedTooltip, FlexContainer, Text, theme } from "@nordcloud/gnui";
import { GetEventsViewQuery } from "~/generated/graphql";
import { ExpandIcon } from "~/components";
import { TooltipText } from "~/components/Styled/TooltipText";
import { TableWrapper } from "~/components/Tables";
import { NameWithLink } from "~/components/Tables/cells/NameWithLink";
import { dateFormat } from "~/constants";
import { ROUTES } from "~/routing/routes";
import { convertMinutesToTime, RefetchOptions } from "~/views/events/helpers";
import { EventDetailRow, EventsTableRow, GridColumns } from "../styled";
import { EntityType, EventEntity } from "../types";
import { ApprovalBadge, ApprovalBadgeKind } from "./ApprovalBadge";
import { ApproveEvent } from "./ApproveEvent/ApproveEvent";
import { eventEntitiesColumns } from "./EventActionsColumns";
import { EventActionSubscriber } from "./EventActionSubscriber";
import { useEventItem } from "./hooks/useEventItem";
import { TooltipIconStatus } from "./TooltipIconStatus";

type Props = {
  event: NonNullable<GetEventsViewQuery["eventsView"]["result"]>[number];
};

export function EventItem({ event }: Props) {
  const [now, setNow] = useState(new Date());
  const lastRefreshedExpireAtRef = useRef<number | null>(null);

  const {
    ui: { isExpanded, toggleIsExpanded },
    event: {
      eventSource,
      liveEventStatus,
      isEventFinished,
      isEventInProgress,
      updateResourceState,
      refreshEvent,
    },
    entities: { hasEventEntities, eventStatusIconActions },
    table: { data: tableData, rowSelection, setRowSelection, columnsProps },
    approval: {
      pendingApprovalLength,
      approveActions,
      approveBatches,
      hasPendingApproval,
      eventApprovalBadgeMeta,
    },
  } = useEventItem({ event });

  const shouldTickApprovalCountdown =
    hasPendingApproval &&
    eventApprovalBadgeMeta?.kind === ApprovalBadgeKind.leftTime;

  useEffect(() => {
    if (!shouldTickApprovalCountdown) {
      lastRefreshedExpireAtRef.current = null;
      return;
    }

    setNow(new Date());

    const timer = setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [shouldTickApprovalCountdown]);

  useEffect(() => {
    const nearestExpireAtMs =
      eventApprovalBadgeMeta?.kind === ApprovalBadgeKind.leftTime
        ? eventApprovalBadgeMeta.expireAt.getTime()
        : null;

    if (!hasPendingApproval || !nearestExpireAtMs) {
      lastRefreshedExpireAtRef.current = null;
      return;
    }

    if (lastRefreshedExpireAtRef.current === nearestExpireAtMs) {
      return;
    }

    const delayMs = Math.max(0, nearestExpireAtMs - Date.now());
    const timeout = setTimeout(() => {
      lastRefreshedExpireAtRef.current = nearestExpireAtMs;
    }, delayMs);

    return () => {
      clearTimeout(timeout);
    };
  }, [hasPendingApproval, eventApprovalBadgeMeta]);

  const endTime = eventSource.endTime ?? eventSource.estimatedEndTime;

  const endTimeTZ = dayjs(endTime)
    .tz(event.scheduleTimezone ?? "")
    .format(dateFormat.dayMonthYearHourMinute);

  const startTimeTZ = dayjs(event.startTime ?? "")
    .tz(event.scheduleTimezone ?? "")
    .format(dateFormat.dayMonthYearHourMinute);

  const duration = dayjs(endTime)
    .startOf("minutes")
    .diff(dayjs(event.startTime).startOf("minutes"), "minutes");

  return (
    <EventsTableRow
      spacing="spacing02"
      boxStyle="lightGrey"
      data-testid="event-row"
    >
      <Row data-testid={event.name}>
        <Col>
          <GridColumns>
            <FlexContainer justifyContent="center" alignItems="center">
              <When condition={hasEventEntities}>
                <ExpandIcon
                  name="chevronDown"
                  title="chevronDown"
                  isExpanded={isExpanded}
                  onClick={toggleIsExpanded}
                />
              </When>
            </FlexContainer>
            <TooltipIconStatus
              status={liveEventStatus}
              actionLength={pendingApprovalLength}
              type={EntityType.EVENT}
              actions={eventStatusIconActions}
            />
            <FlexContainer css={{ minWidth: 0 }}>
              <NameWithLink
                name={event.name ?? ""}
                link={ROUTES.events.generateDetails(event.id)}
                containerStyle={{ minWidth: 0 }}
              />
              {eventApprovalBadgeMeta && (
                <ApprovalBadge badgeMeta={eventApprovalBadgeMeta} now={now} />
              )}
            </FlexContainer>
            <EventDetailRow alignItems="center" data-label="Start">
              <ExtendedTooltip
                caption={
                  <>
                    Plan Time: {startTimeTZ}
                    <br />
                    Timezone: {event.scheduleTimezone}
                  </>
                }
              >
                <TooltipText
                  size="md"
                  tag="span"
                  mr={theme.spacing.spacing01}
                  weight="regular"
                  color={theme.color.text.text01}
                >
                  {dayjs(event.startTime).format(
                    dateFormat.dayMonthYearHourMinute
                  )}
                </TooltipText>
              </ExtendedTooltip>
            </EventDetailRow>
            <EventDetailRow alignItems="center" data-label="End">
              <ExtendedTooltip
                caption={
                  <If condition={!isEventFinished}>
                    <Then>End Time will appear when the event is complete</Then>
                    <Else>
                      <>
                        Plan Time: {endTimeTZ}
                        <br />
                        Timezone: {event.scheduleTimezone}
                      </>
                    </Else>
                  </If>
                }
              >
                <TooltipText
                  size="md"
                  tag="span"
                  mr={theme.spacing.spacing01}
                  weight="regular"
                  color={theme.color.text.text01}
                >
                  <If condition={!isEventFinished}>
                    <Then>Awaiting completion</Then>
                    <Else>
                      {dayjs(endTime).format(dateFormat.dayMonthYearHourMinute)}
                    </Else>
                  </If>
                </TooltipText>
              </ExtendedTooltip>
            </EventDetailRow>
            <EventDetailRow alignItems="center" data-label="Duration">
              <If condition={isEventFinished}>
                <Then>
                  <Text
                    size="md"
                    tag="span"
                    mr={theme.spacing.spacing01}
                    weight="regular"
                    color={theme.color.text.text01}
                  >
                    {convertMinutesToTime(duration)}
                  </Text>
                </Then>
              </If>
            </EventDetailRow>
            <If condition={hasPendingApproval}>
              <Then>
                <FlexContainer>
                  <ApproveEvent
                    eventId={event.id}
                    hasIcon
                    actions={approveActions}
                    batches={approveBatches}
                    refetchQueries={RefetchOptions.EVENTS}
                  />
                </FlexContainer>
              </Then>
              <Else>
                <div />
              </Else>
            </If>
          </GridColumns>
          <When condition={isExpanded}>
            <TableWrapper<EventEntity>
              loading={false}
              data={tableData}
              columns={eventEntitiesColumns({
                now,
                refreshEvent,
                ...columnsProps,
              })}
              rowSelection={rowSelection}
              selectedRowsHandler={() => null}
              setRowSelection={setRowSelection}
              stripped
            />
          </When>
          <EventActionSubscriber
            eventId={event.id}
            isRunning={isEventInProgress}
            onResourceStateUpdate={updateResourceState}
          />
        </Col>
      </Row>
    </EventsTableRow>
  );
}
